/**
 * Where each object comes back to when it falls out of the world (design D7).
 *
 * A plain mutable module object, deliberately outside React and the store, exactly like
 * `src/input/inputState.ts` and `src/engine/playerTrail.ts`: it is written from inside
 * `useFrame` — every throw and every drop records a home — and read from inside `useFrame`
 * by the sweep that returns what has fallen. Per-frame data must never go through a render
 * (architectural rule 3).
 *
 * It is a module of its own rather than two more fields in `objectRegistry.ts` because a
 * home is a different fact with a different lifetime from "which body is this id": the
 * registry answers for the body that exists right now, and this answers for a pose the
 * body left behind.
 */

import type { RapierRigidBody } from '@react-three/rapier';
import type { Rect, Vec3 } from '../game/profile/types';

/** A pose to come back to. Stored by value — the body it came from will have moved on. */
export type ObjectPose = {
  position: Vec3;
  rotation: { x: number; y: number; z: number; w: number };
};

const homes = new Map<string, ObjectPose>();

/**
 * The level's grass, or null indoors. Set once per level by `LostAndFound`, and the only
 * reason this module knows anything about the world: see `recordObjectHome`.
 */
let lawn: Rect | null = null;

export function setHomeLawn(rect: Rect | null | undefined): void {
  lawn = rect ?? null;
}

/**
 * Records where an object should come back to: the pose it is in right now.
 *
 * Called when an object is registered — so one that has never been carried still comes
 * back, to where the level placed it — and again every time one is released, which is what
 * makes the home "where the player last had it".
 *
 * **Refused if the pose is not over the grass** (design D8). A player standing on the
 * north crest can lean out and throw over the moat; recording that release would be a
 * soft-lock, because the object would be returned to a pose over the water, fall out
 * again, and be returned again forever. The previous home survives instead — the last
 * release that was over grass, or failing that where the level placed it.
 *
 * The test is the footprint only. Height is the kill plane's business, and lawn the player
 * may not *stand* on is still lawn an object may rest on: something dropped from the crest
 * onto the north apron is in plain sight and reachable by the same ladder.
 */
export function recordObjectHome(id: string, body: RapierRigidBody): void {
  const position = body.translation();
  if (!isOverLawn(position)) return;
  const rotation = body.rotation();
  homes.set(id, {
    position: { x: position.x, y: position.y, z: position.z },
    rotation: { x: rotation.x, y: rotation.y, z: rotation.z, w: rotation.w },
  });
}

export function objectHome(id: string): ObjectPose | undefined {
  return homes.get(id);
}

export function forgetObjectHome(id: string): void {
  homes.delete(id);
}

/** Emptied when a level goes away: a pose in the last level means nothing in this one. */
export function clearObjectHomes(): void {
  homes.clear();
}

function isOverLawn(position: Vec3): boolean {
  if (lawn === null) return true;
  return (
    position.x >= lawn.x &&
    position.x <= lawn.x + lawn.width &&
    position.z >= lawn.z &&
    position.z <= lawn.z + lawn.depth
  );
}
